import { isElectronRuntime } from '../realm/isRealmEnabled';

const KIND_WORKFLOW = 'WORKFLOW';
const KIND_TASK = 'TASK';

const normalizeKind = (value) => {
  const kind = String(value || '').trim().toUpperCase();
  if (kind === KIND_TASK || kind === 'TASKS') return KIND_TASK;
  if (kind === KIND_WORKFLOW || kind === 'WF') return KIND_WORKFLOW;
  return '';
};

export const normalizeWorkItemShare = (value) => {
  if (!value || typeof value !== 'object') return null;
  const kind = normalizeKind(value.kind);
  const itemId = String(value.itemId || value.id || '').trim();
  if (!kind || !itemId) return null;
  return {
    kind,
    itemId,
    title: String(value.title || '').trim(),
    channelId: String(value.channelId || '').trim(),
  };
};

const decodeShareMarkup = (value) => String(value || '')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#39;/g, "'")
  .replace(/<[^>]*>/g, ' ');

const matchQueryParam = (text, name) => {
  const match = String(text || '').match(new RegExp(`[?&#]${name}=([^&\\s#"']+)`, 'i'));
  if (!match) return '';
  try {
    return decodeURIComponent(String(match[1] || '').replace(/\+/g, ' ').trim());
  } catch {
    return String(match[1] || '').replace(/\+/g, ' ').trim();
  }
};

const plainSnippet = (value, fallback = '') => {
  const text = String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return fallback;
  return text.length > 80 ? `${text.slice(0, 79)}…` : text;
};

export const resolveWorkItemShareTitle = (share, { tasks = [], messages = [] } = {}) => {
  const normalized = normalizeWorkItemShare(share);
  if (!normalized) return '';
  if (normalized.title) return normalized.title;
  if (normalized.kind === KIND_TASK) {
    const task = (tasks || []).find((item) => String(item?.id) === String(normalized.itemId));
    const titled = String(task?.title || '').trim();
    if (titled) return titled;
    return plainSnippet(task?.description, '');
  }
  const message = (messages || []).find((item) => String(item?.id || item?._id) === String(normalized.itemId));
  const workflowTitle = String(message?.workflowTitle || '').trim();
  if (workflowTitle) return workflowTitle;
  return plainSnippet(message?.content || message?.contentHtml, '');
};

export const withWorkItemShareTitle = (share, lookup) => {
  const normalized = normalizeWorkItemShare(share);
  if (!normalized) return share;
  const title = resolveWorkItemShareTitle(normalized, lookup);
  return title ? { ...normalized, title } : normalized;
};

export const parseWorkItemShareFromText = (value) => {
  const text = decodeShareMarkup(value);
  if (!text) return null;
  const match = text.match(/[?&#](workflow|task)=([^&\s#"']+)/i)
    || text.match(/\/chat\/(workflow|task)\/([^/?#\s]+)/i);
  if (!match) return null;
  const kind = normalizeKind(match[1]);
  const itemId = decodeURIComponent(String(match[2] || '').trim());
  if (!kind || !itemId) return null;
  return {
    kind,
    itemId,
    title: matchQueryParam(text, 'title'),
    channelId: matchQueryParam(text, 'channelId'),
  };
};

export const resolveWorkItemShare = (msg) => (
  normalizeWorkItemShare(msg?.workItemShare)
  || parseWorkItemShareFromText(msg?.linkPreview?.url)
  || parseWorkItemShareFromText(msg?.content)
);

export const buildWorkItemShareUrl = (value) => {
  const share = normalizeWorkItemShare(value);
  if (!share) return '';
  const param = share.kind === KIND_TASK ? 'task' : 'workflow';
  const params = new URLSearchParams();
  params.set(param, share.itemId);
  if (share.title) params.set('title', share.title);
  if (share.channelId) params.set('channelId', share.channelId);
  const query = params.toString();
  if (typeof window === 'undefined') return `/chat?${query}`;
  if (isElectronRuntime()) {
    const path = window.location.pathname || '/';
    return `${window.location.origin}${path}#/chat?${query}`;
  }
  return `${window.location.origin}/chat?${query}`;
};

export const workItemShareLabel = (share) => {
  const normalized = normalizeWorkItemShare(share);
  if (!normalized) return 'Shared item';
  if (normalized.title) return normalized.title;
  return normalized.kind === KIND_TASK ? 'Task' : 'Workflow';
};

export const WORK_ITEM_SHARE_KIND = { WORKFLOW: KIND_WORKFLOW, TASK: KIND_TASK };
